import { BuildingConfigService } from './BuildingConfigService';

export interface LiveBmsReading {
  facility_id: string;
  point_id: string;
  value: number;
  timestamp: number;
  quality: string;
  source: string;
  unit?: string | null;
  protocol?: string | null;
  equipment?: string | null;
  zone?: string | null;
  stale?: boolean;
  age_ms?: number;
}

export interface LiveBmsStatus {
  connected: boolean;
  pointCount: number;
  totalPointCount: number;
  staleCount: number;
  lastUpdate: number | null;
  responseAt: number | null;
  error: string | null;
}

type Listener = (status: LiveBmsStatus) => void;

const DEFAULT_POLL_MS = 5000;
const DEFAULT_MAX_AGE_SEC = 300;

class _LiveBmsService {
  private readings = new Map<string, LiveBmsReading>();
  private timer: ReturnType<typeof setInterval> | null = null;
  private listeners = new Set<Listener>();
  private status: LiveBmsStatus = {
    connected: false,
    pointCount: 0,
    totalPointCount: 0,
    staleCount: 0,
    lastUpdate: null,
    responseAt: null,
    error: null,
  };

  start(intervalMs = DEFAULT_POLL_MS) {
    if (this.timer) return;
    void this.refresh();
    this.timer = setInterval(() => void this.refresh(), intervalMs);
  }

  stop() {
    if (!this.timer) return;
    clearInterval(this.timer);
    this.timer = null;
  }

  async refresh() {
    try {
      const facilityId = BuildingConfigService.get().profile?.facilityId
        || BuildingConfigService.get().meta?.id
        || 'sedc-01';
      const query = new URLSearchParams({
        facility: facilityId,
        maxAgeSec: String(DEFAULT_MAX_AGE_SEC),
      });
      const response = await fetch(`/api/live-bems/snapshot?${query.toString()}`, {
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`Live BEMS request failed: ${response.status}`);
      const payload = await response.json();
      const incoming: LiveBmsReading[] = Array.isArray(payload.readings) ? payload.readings : [];
      this.readings.clear();
      for (const reading of incoming) {
        if (typeof reading?.point_id !== 'string') continue;
        const value = Number(reading.value);
        if (!Number.isFinite(value)) continue;
        const timestamp = Number(reading.timestamp) || Date.now();
        const quality = String(reading.quality || 'good').toLowerCase();
        if (reading.stale || quality === 'bad' || quality === 'invalid' || quality === 'error') continue;
        this.readings.set(reading.point_id, {
          ...reading,
          value,
          timestamp,
        });
      }
      const newestTimestamp = Number(payload.newest_timestamp);
      const responseAt = Number(payload.timestamp) || Date.now();
      this.setStatus({
        connected: true,
        pointCount: this.readings.size,
        totalPointCount: incoming.length,
        staleCount: Number(payload.stale_count ?? 0),
        lastUpdate: Number.isFinite(newestTimestamp) ? newestTimestamp : null,
        responseAt,
        error: null,
      });
    } catch (error) {
      this.setStatus({
        ...this.status,
        connected: false,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  get(pointId: string): number | null {
    return this.readings.get(pointId)?.value ?? null;
  }

  getReading(pointId: string): LiveBmsReading | null {
    return this.readings.get(pointId) ?? null;
  }

  getSnapshot(): Record<string, number> {
    return Object.fromEntries(
      Array.from(this.readings.entries()).map(([pointId, reading]) => [pointId, reading.value]),
    );
  }

  getStatus(): LiveBmsStatus {
    return { ...this.status };
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.getStatus());
    return () => this.listeners.delete(listener);
  }

  private setStatus(status: LiveBmsStatus) {
    this.status = status;
    const copy = this.getStatus();
    this.listeners.forEach(listener => listener(copy));
  }
}

export const LiveBmsService = new _LiveBmsService();
